import { api } from '../../../api/client';
import type {
  SuboutEddImportOutcome, SuboutResultDeleteOutcome, SuboutResultEdit, SuboutResultsListRow,
  SuboutResultsLookups, SuboutResultsMode, SuboutResultsOrder, SuboutResultsView,
} from './types';

// Thin axios wrappers over /api/v1/subout-results. The shared client injects the
// JWT + global loader. Every rule runs in the API (SuboutResultRules).

const BASE = '/api/v1/subout-results';

type Ok<T> = { data: T };

/** One of the lists; `sinceDate` (yyyy-MM-dd) is the Date Filter bound (entry + po). */
export async function fetchSuboutResultsList(view: SuboutResultsView, sinceDate?: string | null): Promise<SuboutResultsListRow[]> {
  const res = await api.get(`${BASE}/list`, { params: { view, sinceDate: sinceDate || undefined } });
  return (res.data as Ok<SuboutResultsListRow[]>).data ?? [];
}

/** One order (header + sample rows + QC rows) for a detail view. */
export async function fetchSuboutResultsOrder(id: string, mode: SuboutResultsMode): Promise<SuboutResultsOrder> {
  const res = await api.get(`${BASE}/order/${id}`, { params: { mode } });
  return (res.data as Ok<SuboutResultsOrder>).data;
}

/** Units + employees for the editors. */
export async function fetchSuboutResultsLookups(): Promise<SuboutResultsLookups> {
  const res = await api.get(`${BASE}/lookups`);
  return (res.data as Ok<SuboutResultsLookups>).data;
}

export interface SaveEntryRequest {
  samples: SuboutResultEdit[];
  qcSamples: SuboutResultEdit[];
  receivedByOid: string | null;
  dateReceived: string | null;
  dateShipped: string | null;
  daysElapsed: number | null;
  remark: string | null;
  lockVersion: number | null;
}

/** Result Entry → Save. */
export async function saveSuboutResultEntry(id: string, req: SaveEntryRequest): Promise<{ samplesSaved: number; qcSaved: number }> {
  const res = await api.put(`${BASE}/entry/${id}`, req);
  return (res.data as Ok<{ samplesSaved: number; qcSaved: number }>).data;
}

/** Result Entry → Submit the selected rows. */
export async function submitSuboutResults(id: string, sampleOids: string[], qcOids: string[]):
  Promise<{ samplesSubmitted: number; qcSubmitted: number; orderCompleted: boolean; reviewLevel: number; newStatus: number }> {
  const res = await api.post(`${BASE}/entry/${id}/submit`, { sampleOids, qcOids });
  return (res.data as Ok<{ samplesSubmitted: number; qcSubmitted: number; orderCompleted: boolean; reviewLevel: number; newStatus: number }>).data;
}

/** Result Entry list → Delete (bulk). */
export async function deleteSuboutResultOrders(orderOids: string[]): Promise<{ deleted: number; rows: SuboutResultDeleteOutcome[] }> {
  const res = await api.post(`${BASE}/entry/delete`, orderOids);
  return (res.data as Ok<{ deleted: number; rows: SuboutResultDeleteOutcome[] }>).data;
}

/** Result Entry → EDD Import (multipart). */
export async function importSuboutEdd(id: string, file: File): Promise<SuboutEddImportOutcome> {
  const form = new FormData();
  form.append('file', file);
  const res = await api.post(`${BASE}/entry/${id}/import`, form);
  return (res.data as Ok<SuboutEddImportOutcome>).data;
}

/** Result Validation → Review (grid edits + the selected rows). */
export async function reviewSuboutResults(id: string, req: {
  samples: SuboutResultEdit[]; qcSamples: SuboutResultEdit[]; sampleOids: string[]; qcOids: string[];
}): Promise<{ samplesReviewed: number; qcReviewed: number; orderCompleted: boolean; level3: boolean }> {
  const res = await api.post(`${BASE}/validation/${id}/review`, req);
  return (res.data as Ok<{ samplesReviewed: number; qcReviewed: number; orderCompleted: boolean; level3: boolean }>).data;
}

/** Result Validation → Rollback the selected rows with a reason. */
export async function rollbackSuboutResults(id: string, sampleOids: string[], qcOids: string[], reason: string):
  Promise<{ samplesRolledBack: number; qcRolledBack: number }> {
  const res = await api.post(`${BASE}/validation/${id}/rollback`, { sampleOids, qcOids, reason });
  return (res.data as Ok<{ samplesRolledBack: number; qcRolledBack: number }>).data;
}
